import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import { Row, Column } from 'mage-components';

import { Frame, List, SwitchToggle, Typography } from '@klarna/bubble-ui';

import ExpandableFrame from './ExpandableFrame';

import { getMerchantAwareTranslator } from '@/selectors/merchant';
import { OPTIONS_META } from '@/constants/countries';

const componentPropTypes = {
  currentConfig: PropTypes.shape({
    DOCUMENTS: PropTypes.object,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

type ComponentProps = PropTypes.InferProps<typeof componentPropTypes>;

const NoDocumentsRequired = () => (
  <List>
    <List.Item>No documents required</List.Item>
  </List>
);

export const DocumentRequirementsComponent: React.FC<ComponentProps> = ({ currentConfig, t }) => {
  const [hideEmpty, setHideEmpty] = useState(true);

  const DocumentSection: React.FC<{ configKey: string }> = ({ configKey }) => {
    const configuration: { attachmentType: string; required: string }[] = Object.values(
      _get(currentConfig?.DOCUMENTS, configKey, {}),
    );

    return hideEmpty && !configuration?.length ? null : (
      <>
        <Row>
          <Column columns={{ mobile: 12 }}>
            <Typography textToken="text-style/text/labels/body/bold">
              {_get(OPTIONS_META.DOCUMENTS, `${configKey}.DESCRIPTION`)}
            </Typography>
          </Column>
        </Row>
        <Row>
          <Column columns={{ mobile: 12 }}>
            <Typography textToken="text-style/text/paragraphs/body/regular">
              {configuration && configuration.length ? (
                <List>
                  {configuration?.map((c, i) => {
                    return (
                      <List.Item key={i}>
                        {t(
                          `products-fe.containers.documents.explanation.${c.attachmentType}.title`,
                        )}
                        :{c.required ? 'Mandatory' : 'Optional'}
                      </List.Item>
                    );
                  })}
                </List>
              ) : (
                <NoDocumentsRequired />
              )}
            </Typography>
          </Column>
        </Row>
      </>
    );
  };

  DocumentSection.propTypes = {
    configKey: PropTypes.string.isRequired,
  };

  return (
    <Frame>
      <Row>
        <Column columns={{ mobile: 8 }}>
          <Typography textToken="text-style/headings/titles/bold/tertiary">Documents</Typography>
        </Column>
        <Column columns={{ mobile: 4 }}>
          <SwitchToggle legal onChange={(v: boolean) => setHideEmpty(v)} checked={hideEmpty}>
            Hide empty items
          </SwitchToggle>
        </Column>
      </Row>
      <ExpandableFrame title="Business">
        <DocumentSection configKey="BUSINESS.ALL" />
        <DocumentSection configKey="BUSINESS.REGISTRATION.true" />
        <DocumentSection configKey="BUSINESS.REGISTRATION.false" />
        {/* @ts-expect-error: FIXME */}
        {OPTIONS_META.DOCUMENTS?.BUSINESS?.ENTITY_TYPE?.length ||
          (!hideEmpty && (
            <>
              <Row>
                <Column columns={{ mobile: 12 }}>
                  <Typography textToken="text-style/text/labels/body/bold">
                    Based on entity type
                  </Typography>
                </Column>
              </Row>
              {/* @ts-expect-error: FIXME */}
              {OPTIONS_META.DOCUMENTS?.BUSINESS?.ENTITY_TYPE?.length ? (
                Object.keys(OPTIONS_META.DOCUMENTS?.BUSINESS?.ENTITY_TYPE).map(e => (
                  <DocumentSection key={e} configKey={`BUSINESS.ENTITY_TYPE.${e}`} />
                ))
              ) : (
                <NoDocumentsRequired />
              )}
            </>
          ))}
      </ExpandableFrame>
      <ExpandableFrame title="Stakeholders">
        <DocumentSection configKey="STAKEHOLDERS.ALL" />
        {OPTIONS_META.DOCUMENTS?.STAKEHOLDERS?.ROLES?.length ||
          (!hideEmpty && (
            <>
              <Row>
                <Column columns={{ mobile: 12 }}>
                  <Typography textToken="text-style/text/labels/body/bold">
                    Based on stakeholder role
                  </Typography>
                </Column>
              </Row>
              {OPTIONS_META.DOCUMENTS?.STAKEHOLDERS?.ROLES?.length ? (
                Object.keys(OPTIONS_META.DOCUMENTS?.STAKEHOLDERS?.ROLES).map(e => (
                  <DocumentSection key={e} configKey={`STAKEHOLDERS.ROLES.${e}`} />
                ))
              ) : (
                <NoDocumentsRequired />
              )}
            </>
          ))}
      </ExpandableFrame>
    </Frame>
  );
};

DocumentRequirementsComponent.propTypes = componentPropTypes;

export const DocumentRequirements = connect((state: any) => ({
  t: getMerchantAwareTranslator(state),
}))(DocumentRequirementsComponent);
